/*
 * Header mobile navigation.
 *
 * Toggles the single `.p-header__nav` panel (the same markup that renders inline
 * on desktop) open/closed below the lg breakpoint. Desktop dropdowns are pure
 * CSS — this only drives the hamburger panel.
 *
 * The panel is a non-modal disclosure dropdown (it sits below the bar and does
 * not cover the page), so it follows the WAI-ARIA disclosure pattern: a button
 * with aria-expanded, closeable on Escape and on outside click — deliberately
 * NO focus trap, which belongs to modal dialogs, not nav disclosures.
 */

const SELECTOR_TOGGLE = '.p-header__toggle';
const SELECTOR_NAV = '.p-header__nav';
const CLASS_OPEN = 'is-open';

// Keep in sync with $lg in src/scss/abstracts/_variables-grid.scss.
const DESKTOP_QUERY = '(min-width: 992px)';

/**
 * Wire up the hamburger toggle for the header navigation panel.
 *
 * @return {void}
 */
const initHeaderNav = () => {

	const toggle = document.querySelector( SELECTOR_TOGGLE );
	const nav = document.querySelector( SELECTOR_NAV );

	if ( !toggle || !nav ) {

		return;

	}

	/**
	 * Collapse the navigation panel.
	 *
	 * @return {void}
	 */
	const close = () => {

		nav.classList.remove( CLASS_OPEN );
		toggle.setAttribute( 'aria-expanded', 'false' );
		toggle.setAttribute( 'aria-label', toggle.dataset.labelOpen || toggle.getAttribute( 'aria-label' ) );

	};

	/**
	 * Expand the navigation panel.
	 *
	 * @return {void}
	 */
	const open = () => {

		nav.classList.add( CLASS_OPEN );
		toggle.setAttribute( 'aria-expanded', 'true' );
		toggle.setAttribute( 'aria-label', toggle.dataset.labelClose || toggle.getAttribute( 'aria-label' ) );

	};

	toggle.addEventListener( 'click', () => {

		if ( nav.classList.contains( CLASS_OPEN ) ) {

			close();

		} else {

			open();

		}

	} );

	// Close after following a menu link.
	nav.addEventListener( 'click', ( event ) => {

		if ( event.target.closest( 'a' ) ) {

			close();

		}

	} );

	// Close on Escape and return focus to the toggle.
	document.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Escape' && nav.classList.contains( CLASS_OPEN ) ) {

			close();
			toggle.focus();

		}

	} );

	// Close when interacting outside the panel (the toggle keeps its own click).
	document.addEventListener( 'pointerdown', ( event ) => {

		if ( !nav.classList.contains( CLASS_OPEN ) ) {

			return;

		}

		if ( !nav.contains( event.target ) && !toggle.contains( event.target ) ) {

			close();

		}

	} );

	// Reset state when crossing into the desktop layout (panel becomes inline).
	const desktop = window.matchMedia( DESKTOP_QUERY );

	desktop.addEventListener( 'change', ( event ) => {

		if ( event.matches ) {

			close();

		}

	} );

};

if ( document.readyState !== 'loading' ) {

	initHeaderNav();

} else {

	document.addEventListener( 'DOMContentLoaded', initHeaderNav );

}
